import { useMutation } from "@apollo/client/react"
import React, { useState } from "react"

import { UploadImageDocument, ImageKind, Associated } from "@/graphql/graphql"
import i18n from "@/i18n"
import { showErrorToast } from "@/shared/toast"

import AddPhotoButton from "./add_photo_button"
import PhotoItem from "./photo_item"
import PhotoPreview from "./photo_preview"
import UploadingPhotoItem from "./uploading_photo_item"

interface VenuePhotosProps {
  venue: {
    id: string
    images?: Array<{
      id: string
      url?: string | null
      kind: string
    }> | null
  }
  onRefetch: () => void
}

interface UploadingPhoto {
  id: string
  file: File
  previewUrl: string
  uploading: boolean
  error: boolean
}

const STYLES = {
  container: "bg-white rounded-lg p-6",
  header: "text-xl font-semibold mb-4 mt-0",
  grid: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
} as const

const VenuePhotos: React.FC<VenuePhotosProps> = ({ venue, onRefetch }) => {
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)
  const [uploadingPhotos, setUploadingPhotos] = useState<UploadingPhoto[]>([])
  const [uploadImageMutation] = useMutation(UploadImageDocument)

  const venuePhotos = venue?.images?.filter((img) => img.kind === "venue_photo") || []

  const uploadPhoto = async (file: File, uploadId: string) => {
    try {
      const result = await uploadImageMutation({
        variables: {
          image: file,
          kind: ImageKind.VenuePhoto,
          associatedId: venue.id,
          associatedType: Associated.Venue,
        },
      })

      if (result.data?.uploadImage?.success) {
        setUploadingPhotos((prev) => prev.filter((p) => p.id !== uploadId))
        onRefetch()
      } else {
        setUploadingPhotos((prev) => prev.map((p) => (p.id === uploadId ? { ...p, uploading: false, error: true } : p)))
      }
    } catch (err) {
      showErrorToast("Failed to upload photo. Please try again.")
      console.error("Error uploading image:", err)
      setUploadingPhotos((prev) => prev.map((p) => (p.id === uploadId ? { ...p, uploading: false, error: true } : p)))
    }
  }

  const handleFileSelect = (file: File) => {
    const uploadId = `upload-${Date.now()}`
    const previewUrl = URL.createObjectURL(file)

    setUploadingPhotos((prev) => [
      ...prev,
      {
        id: uploadId,
        file,
        previewUrl,
        uploading: true,
        error: false,
      },
    ])

    void uploadPhoto(file, uploadId)
  }

  const handleRetry = (uploadingPhoto: UploadingPhoto) => {
    setUploadingPhotos((prev) =>
      prev.map((p) => (p.id === uploadingPhoto.id ? { ...p, uploading: true, error: false } : p)),
    )
    void uploadPhoto(uploadingPhoto.file, uploadingPhoto.id)
  }

  return (
    <>
      <div className={STYLES.container} data-testid="settings-venue-photos-container">
        <h2 className={STYLES.header}>{i18n.t("app.settings.venues.tabbio.photos.header")}</h2>
        <div className={STYLES.grid} data-testid="settings-venue-photos-grid">
          {venuePhotos.map((image) => (
            <PhotoItem
              key={image.id}
              image={image}
              venueId={venue.id}
              onPreview={setPreviewImageUrl}
              onRemoveSuccess={onRefetch}
            />
          ))}
          {uploadingPhotos.map((uploadingPhoto) => (
            <UploadingPhotoItem
              key={uploadingPhoto.id}
              previewUrl={uploadingPhoto.previewUrl}
              uploading={uploadingPhoto.uploading}
              error={uploadingPhoto.error}
              onRetry={() => handleRetry(uploadingPhoto)}
            />
          ))}
          <AddPhotoButton onFileSelect={handleFileSelect} />
        </div>
      </div>

      <PhotoPreview imageUrl={previewImageUrl} onClose={() => setPreviewImageUrl(null)} />
    </>
  )
}

export default VenuePhotos
